import { useState, useEffect } from 'react';
import { authFetch } from '@/utils/authFetch';
import Layout from '@/components/Layout';
import { Card, CardContent } from '@/components/ui/card';
import { Activity as ActivityIcon, FolderKanban, CheckSquare, Building, Calendar, FileText, FolderOpen, UserCog, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Activity() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/activities`);

      if (response.ok) {
        const data = await response.json();
        setActivities(data);
      }
    } catch (error) {
      toast.error('Failed to load activities');
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (entityType) => {
    const icons = {
      project: FolderKanban,
      task: CheckSquare,
      client: Building,
      meeting: Calendar,
      document: FolderOpen,
      employee: UserCog,
      report: FileText
    };
    const Icon = icons[entityType] || ActivityIcon;
    return <Icon className="w-5 h-5" />;
  };

  const getActionColor = (action) => {
    const colors = {
      created: 'text-emerald-600 bg-emerald-50',
      updated: 'text-blue-600 bg-blue-50',
      deleted: 'text-red-600 bg-red-50',
      completed: 'text-purple-600 bg-purple-50'
    };
    return colors[action] || 'text-slate-600 bg-slate-50';
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Activity Timeline</h1>
          <p className="text-slate-600 mt-1">Company-wide activity feed</p>
        </div>

        <div className="space-y-3">
          {activities.map((activity) => (
            <Card key={activity.activity_id} className="card-hover">
              <CardContent className="p-4">
                <div className="flex items-start space-x-4">
                  <div className={`p-2 rounded-md ${getActionColor(activity.action)}`}>
                    {getIcon(activity.entity_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">
                          <span className="font-semibold">{activity.user_name}</span>
                          {' '}
                          <span className="text-slate-600">{activity.action}</span>
                          {' '}
                          <span className="font-semibold">{activity.entity_type}</span>
                        </p>
                        <p className="text-sm text-slate-600 mt-1">{activity.description}</p>
                      </div>
                      <span className="text-xs text-slate-500 whitespace-nowrap ml-4">
                        {formatTime(activity.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-indigo-600 font-medium mt-2">{activity.entity_name}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {activities.length === 0 && (
            <Card>
              <CardContent className="py-12">
                <div className="text-center text-slate-500">
                  <ActivityIcon className="w-12 h-12 mx-auto mb-4 text-slate-400" />
                  <p>No activities yet</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
}
